import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stack,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LocalShipping as DeliveryIcon } from '@mui/icons-material';
import type { BulkChannelsEditorProps } from './types';
import { CHANNEL_SNOOZE_PRESETS, DEFAULT_SNOOZE_PRESET } from '../channelSnoozePresets';
import { useTimeFormat } from '../../../context/TimeFormatContext';
import { formatSnoozeDateTime } from '../../../utils/formatTime';

export const BulkChannelsEditor: React.FC<BulkChannelsEditorProps> = ({
  channelsData,
  onChannelsDataChange,
  availableChannels,
  isLoadingChannels,
}) => {
  const { use24HourFormat } = useTimeFormat();

  const updateAggregator = (
    channel: string,
    updates: Partial<{
      enabled: boolean;
      include?: boolean;
      deactivationReason?: string;
      deactivationExpiry?: Date;
      quickOption?: string;
    }>
  ) => {
    onChannelsDataChange({
      ...channelsData,
      aggregators: {
        ...channelsData.aggregators,
        [channel]: {
          ...channelsData.aggregators[channel],
          ...updates,
        },
      },
    });
  };

  const isOnlineOrdersIncluded = channelsData.includeOnlineOrders ?? false;
  // When the staged update sets Online Orders to OFF, the rule is that every
  // aggregator must also go OFF — otherwise we'd ship the broken
  // "online OFF + aggregator ON" state Byte Commerce rejects.
  const isForcingOnlineOrdersOff =
    isOnlineOrdersIncluded && channelsData.acceptingOnlineOrders === false;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Check the items you want to modify. Unchecked items will not be changed.
        <br />
        <Typography variant="caption" component="span" sx={{ mt: 0.5, display: 'block' }}>
          Delivery aggregator changes may take a few minutes to reflect. Online Orders changes are
          immediate.
        </Typography>
      </Alert>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          opacity: isOnlineOrdersIncluded ? 1 : 0.5,
          bgcolor: isOnlineOrdersIncluded ? 'background.paper' : 'action.hover',
          transition: 'opacity 0.2s, background-color 0.2s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={isOnlineOrdersIncluded}
            onChange={(e) =>
              onChannelsDataChange({
                ...channelsData,
                includeOnlineOrders: e.target.checked,
              })
            }
            sx={{ p: 0.5 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={channelsData.acceptingOnlineOrders}
                onChange={(e) => {
                  const nextAcceptingOnlineOrders = e.target.checked;
                  // Turning Online Orders OFF while it's included in the bulk
                  // update cascades every aggregator to enabled:false (and auto-
                  // includes them) so the resulting bulk write can't leave
                  // "online OFF + aggregator ON" anywhere.
                  const shouldCascadeOff = isOnlineOrdersIncluded && !nextAcceptingOnlineOrders;
                  const nextAggregators = shouldCascadeOff
                    ? availableChannels.reduce(
                        (acc, ch) => {
                          acc[ch.channel] = {
                            ...channelsData.aggregators[ch.channel],
                            include: true,
                            enabled: false,
                          };
                          return acc;
                        },
                        { ...channelsData.aggregators } as typeof channelsData.aggregators
                      )
                    : channelsData.aggregators;
                  onChannelsDataChange({
                    ...channelsData,
                    acceptingOnlineOrders: nextAcceptingOnlineOrders,
                    // Disabling always carries a reactivate-at time — default to
                    // 1H (same as aggregator pauses); clear it when re-enabling.
                    onlineOrdersReactivateAt: shouldCascadeOff
                      ? DEFAULT_SNOOZE_PRESET.getValue()
                      : undefined,
                    onlineOrdersQuickOption: shouldCascadeOff
                      ? DEFAULT_SNOOZE_PRESET.key
                      : undefined,
                    aggregators: nextAggregators,
                  });
                }}
                disabled={!isOnlineOrdersIncluded}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Accept Online Orders (Website, Mobile App, and Delivery Aggregators)</span>
                {!isOnlineOrdersIncluded && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    (won't be modified)
                  </Typography>
                )}
              </Box>
            }
            labelPlacement="start"
            sx={{ flex: 1, justifyContent: 'space-between', ml: 0, mr: 0 }}
          />
        </Box>

        {/* Disabling Online Orders is never indefinite — pick when it turns back ON. */}
        {isForcingOnlineOrdersOff && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Turn back on after
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1, mb: 1.5 }}>
              {CHANNEL_SNOOZE_PRESETS.map((preset) => {
                const isSelected = channelsData.onlineOrdersQuickOption === preset.key;
                return (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    onClick={() =>
                      onChannelsDataChange({
                        ...channelsData,
                        onlineOrdersReactivateAt: preset.getValue(),
                        onlineOrdersQuickOption: preset.key,
                      })
                    }
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    size="small"
                  />
                );
              })}
            </Stack>
            {/* Preset chips only — no free date/time entry, so the expiry can
                never land past the 5 AM ceiling. */}
            {channelsData.onlineOrdersReactivateAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Online Orders (and the paused aggregators) will automatically turn back ON at{' '}
                {formatSnoozeDateTime(channelsData.onlineOrdersReactivateAt, use24HourFormat)}.
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 2 }}>
        <DeliveryIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2">Delivery Aggregators</Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoadingChannels ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : availableChannels.length === 0 ? (
        <Alert severity="info">No delivery channels are configured for this organization.</Alert>
      ) : (
        <Stack spacing={2}>
          {availableChannels.map((channel) => {
            const isEnabled = channelsData.aggregators[channel.channel]?.enabled ?? true;
            const channelData = channelsData.aggregators[channel.channel];
            const isIncluded = channelData?.include ?? false;

            return (
              <Paper
                key={channel.channel}
                sx={{
                  p: 2,
                  opacity: isIncluded ? 1 : 0.5,
                  bgcolor: isIncluded ? 'background.paper' : 'action.hover',
                  transition: 'opacity 0.2s, background-color 0.2s',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={isIncluded}
                      onChange={(e) =>
                        updateAggregator(channel.channel, { include: e.target.checked })
                      }
                      disabled={isForcingOnlineOrdersOff}
                      sx={{ p: 0.5 }}
                    />
                    {!isEnabled && isIncluded && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'error.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 2,
                            bgcolor: 'error.main',
                            transform: 'rotate(-45deg)',
                          }}
                        />
                      </Box>
                    )}
                    <Typography variant="body1" fontWeight={500}>
                      {channel.displayName}
                    </Typography>
                    {!isIncluded && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        (won't be modified)
                      </Typography>
                    )}
                    {isForcingOnlineOrdersOff && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        Forced OFF because Online Orders is being disabled
                      </Typography>
                    )}
                    {!isEnabled && isIncluded && (
                      <Chip
                        label="Inactive"
                        size="small"
                        sx={{
                          bgcolor: 'error.50',
                          color: 'error.main',
                          border: '1px solid',
                          borderColor: 'error.200',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Box>
                  <Switch
                    checked={isEnabled}
                    onChange={(e) =>
                      updateAggregator(channel.channel, {
                        enabled: e.target.checked,
                        // Clear expiry when re-enabling
                        ...(e.target.checked && { deactivationExpiry: undefined }),
                      })
                    }
                    disabled={!isIncluded || isForcingOnlineOrdersOff}
                  />
                </Box>

                {!isEnabled && !isForcingOnlineOrdersOff && (
                  <Box sx={{ mt: 2 }}>
                    {/* Preset chips only for the expiry — no free date/time entry,
                        so it can never land past the 5 AM ceiling. */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Reason (optional)"
                          size="small"
                          fullWidth
                          value={channelData?.deactivationReason || ''}
                          onChange={(e) =>
                            updateAggregator(channel.channel, {
                              deactivationReason: e.target.value,
                            })
                          }
                          placeholder="e.g., Kitchen maintenance"
                        />
                      </Grid>
                    </Grid>

                    {/* Quick Options */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Quick Options
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {/* Short-duration options */}
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {CHANNEL_SNOOZE_PRESETS.map((preset) => {
                            const isSelected = channelData?.quickOption === preset.key;
                            return (
                              <Chip
                                key={preset.key}
                                label={preset.label}
                                onClick={() => {
                                  updateAggregator(channel.channel, {
                                    deactivationExpiry: preset.getValue(),
                                    quickOption: preset.key,
                                  });
                                }}
                                variant={isSelected ? 'filled' : 'outlined'}
                                color={isSelected ? 'primary' : 'default'}
                                size="small"
                              />
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Box>

                    {channelData?.deactivationExpiry && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Channel will reactivate:{' '}
                        {formatSnoozeDateTime(channelData.deactivationExpiry, use24HourFormat)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
